import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Factory, Palette, ShieldCheck, Truck, Headphones, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { api, img, useSeo } from "@/lib/api";
import ProductCard from "@/components/store/ProductCard";

function Hero({ banners }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % banners.length), 6500);
    return () => clearInterval(t);
  }, [banners.length]);
  const b = banners[i] || { title: "Suas ideias ganham forma.", text: "Produtos criativos, personalizados e produzidos em impressão 3D.", image_desktop: "/demo/hero.jpg" };
  return (
    <section className="relative overflow-hidden min-h-[520px] lg:min-h-[680px] flex items-end lg:items-center" data-testid="hero">
      <picture key={i} className="absolute inset-0">
        <source media="(max-width: 767px)" srcSet={img(b.image_mobile || b.image_desktop)} />
        <img src={img(b.image_desktop)} alt={b.title} className="h-full w-full object-cover opacity-60 rise" fetchPriority="high" />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-[#050608] via-[#050608]/85 to-transparent" />
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="relative max-w-[1440px] mx-auto w-full px-4 lg:px-8 pb-10 lg:pb-14 pt-24 lg:pt-32">
        <div className="max-w-2xl" key={`t${i}`}>
          <p className="eyebrow rise">E³ Impressão 3D · Produção própria</p>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mt-5 leading-[1.02] rise d1">{b.title}</h1>
          <p className="text-base lg:text-lg text-[#E6E6E6] mt-6 max-w-lg rise d2">{b.text}</p>
          <div className="flex flex-col sm:flex-row gap-3 mt-10 rise d3">
            <Link to={b.link || "/produtos"} className="btn-primary px-8 py-4" data-testid="hero-buy-now-button">{b.button || "COMPRAR AGORA"}<ArrowRight className="h-4 w-4" /></Link>
            <Link to="/personalizados" className="btn-ghost px-8 py-4" data-testid="hero-customize-button">PERSONALIZE O SEU</Link>
          </div>
        </div>
        {banners.length > 1 && (
          <div className="flex items-center gap-3 mt-12">
            <button aria-label="Anterior" className="btn-ghost btn-sm" onClick={() => setI((i - 1 + banners.length) % banners.length)}><ChevronLeft className="h-4 w-4" /></button>
            {banners.map((_, k) => <button key={k} aria-label={`Slide ${k + 1}`} onClick={() => setI(k)} className={`h-1 rounded-full transition-all ${k === i ? "w-10 bg-[#00C8FF]" : "w-5 bg-[#282D37]"}`} />)}
            <button aria-label="Próximo" className="btn-ghost btn-sm" onClick={() => setI((i + 1) % banners.length)}><ChevronRight className="h-4 w-4" /></button>
          </div>
        )}
      </div>
    </section>
  );
}

const BENEFITS = [[Factory, "Produção própria"], [Palette, "Produtos personalizados"], [ShieldCheck, "Pagamento seguro"], [Truck, "Envio para todo o Brasil"], [Headphones, "Atendimento personalizado"]];

function Carousel({ items, testid }) {
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * ref.current.clientWidth * 0.8, behavior: "smooth" });
  return (
    <div className="relative min-w-0 w-full">
      <div className="hidden md:flex gap-2 absolute -top-16 right-0">
        <button className="btn-ghost btn-sm" aria-label="Anterior" onClick={() => scroll(-1)}><ChevronLeft className="h-4 w-4" /></button>
        <button className="btn-ghost btn-sm" aria-label="Próximo" onClick={() => scroll(1)}><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div ref={ref} className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-none pb-2" data-testid={testid}>
        {items.map((p) => <div key={p.id} className="snap-start shrink-0 w-[72%] sm:w-[45%] md:w-[31%] lg:w-[23.5%]"><ProductCard p={p} /></div>)}
      </div>
    </div>
  );
}

export default function Home() {
  const [data, setData] = useState(null);
  useSeo({});
  useEffect(() => { api.get("/store/home").then((r) => setData(r.data)); }, []);
  const banners = (data?.banners || []).filter((b) => (b.placement || "home") === "home");
  return (
    <>
      <Hero banners={banners} />
      <section className="border-y border-[#171A1F] bg-[#0B0D11]" data-testid="benefits-bar">
        <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-6 flex gap-8 overflow-x-auto scrollbar-none lg:justify-between">
          {BENEFITS.map(([I, t]) => (
            <div key={t} className="flex items-center gap-3 shrink-0"><span className="h-10 w-10 rounded-xl grid place-items-center bg-[#008CFF]/10 text-[#00C8FF]"><I className="h-5 w-5" /></span><span className="text-sm font-semibold">{t}</span></div>
          ))}
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-14 lg:py-24">
        <p className="eyebrow">Categorias</p>
        <h2 className="text-3xl lg:text-4xl font-semibold mt-3 mb-10">Explore o universo E³</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="categories-grid">
          {(data?.categories || []).map((c, k) => (
            <Link key={c.id} to={c.slug === "personalizados" ? "/personalizados" : c.slug === "empresas" ? "/empresas" : `/produtos?categoria=${c.slug}`}
              className={`group relative overflow-hidden rounded-2xl border border-[#282D37] hover:border-[#00C8FF]/60 transition-colors ${k === 0 ? "col-span-2 aspect-[16/10] lg:row-span-2 lg:aspect-auto" : "aspect-square"}`} data-testid={`category-card-${c.slug}`}>
              <img src={img(c.image)} alt={c.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050608] via-[#050608]/40 to-transparent" />
              <div className="absolute bottom-0 p-5 lg:p-6">
                <h3 className={`${k === 0 ? "text-2xl lg:text-4xl" : "text-lg lg:text-xl"} font-semibold`}>{c.name}</h3>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#00C8FF] mt-2 group-hover:gap-3 transition-all">Ver produtos <ArrowRight className="h-3.5 w-3.5" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 pb-14 lg:pb-24">
        <p className="eyebrow">Destaques</p>
        <h2 className="text-3xl lg:text-4xl font-semibold mt-3 mb-10">Mais vendidos</h2>
        <Carousel items={data?.bestsellers || []} testid="bestsellers-carousel" />
      </section>

      <section className="relative overflow-hidden border-y border-[#171A1F]" data-testid="gamer-section">
        <div className="absolute inset-0 grid-bg" />
        <div className="absolute -right-40 top-0 h-[500px] w-[500px] rounded-full bg-[#008CFF]/10 blur-3xl" />
        <div className="relative max-w-[1440px] mx-auto px-4 lg:px-8 py-14 lg:py-24 grid lg:grid-cols-[1fr_1.6fr] gap-8 lg:gap-12 items-center">
          <div>
            <p className="eyebrow">E³ GAMER</p>
            <h2 className="text-4xl lg:text-6xl font-bold mt-4 leading-[1.05]">Seu setup.<br /><span className="text-grad">Do seu jeito.</span></h2>
            <p className="text-[#A0A0A0] mt-6 max-w-md">Suportes para controle e headset, organizadores de cabos, gamertags, luminárias e decoração para deixar o seu setup com a sua cara.</p>
            <div className="flex flex-wrap gap-2 mt-6">{["Suporte p/ controle", "Suporte p/ headset", "Organizador de cabos", "Gamertags", "Luminárias", "Suportes de parede"].map((t) => <span key={t} className="chip border border-[#282D37] text-[#E6E6E6] font-mono">{t}</span>)}</div>
            <Link to="/produtos?categoria=gamer" className="btn-primary mt-10" data-testid="gamer-cta">Ver linha gamer <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <Carousel items={data?.gamer || []} testid="gamer-carousel" />
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-14 lg:py-24" data-testid="customize-section">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="relative rounded-3xl overflow-hidden aspect-[4/3] border border-[#282D37]">
            <img src="/demo/printer.jpg" alt="Impressora 3D produzindo uma peça" loading="lazy" className="h-full w-full object-cover" />
          </div>
          <div>
            <p className="eyebrow">Personalize o seu</p>
            <h2 className="text-3xl lg:text-5xl font-bold mt-4">Feito sob medida, camada por camada.</h2>
            <ol className="mt-10 space-y-6">
              {["Escolha o modelo", "Escolha as cores", "Adicione nome ou gamertag", "Finalize seu pedido"].map((s, k) => (
                <li key={s} className="flex items-center gap-5">
                  <span className="font-mono text-sm h-12 w-12 shrink-0 rounded-xl grid place-items-center border border-[#008CFF]/40 text-[#00C8FF]">0{k + 1}</span>
                  <span className="text-lg font-semibold">{s}</span>
                </li>
              ))}
            </ol>
            <Link to="/produtos?customizable=1" className="btn-primary mt-10 px-8 py-4" data-testid="create-product-cta">CRIAR MEU PRODUTO <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
